import { ISubscriptionItem } from './../../../../../../store/models/register.model';
import { Subscription } from 'rxjs';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';

import { RegisterForm, RegisterFormPay } from '../../../../../../store/models/register.model';
import { Store } from '@ngrx/store';
import { PageState } from '../../../../../../store/models/page.model';
import { setBannerComponent } from '../../../../../../store/actions/page.action';
import { InputTooltipOptions } from 'app/store/models/popover.model';
import { ISelectItem } from 'app/store/models/select.model';
import { DiscountsService } from '../../../../../../store/services/discounts.service';
import { IPlanProduct } from '../../../../../../store/models/discount.model';
import { ButtonComponent } from '../../../../../ui/button/button.component';
import { UserService } from 'app/store/services/user.service';
import { StripeEmbeddedService } from 'app/store/services/stripe-embedded.service';
import { paySuccess } from 'app/store/actions/register.action';
import { openSnackbar } from 'app/store/actions/page.action';
import { IReducers } from 'app/store/reducers';
import { environment } from 'environments/environment';
import { getApiErrorMessage } from 'app/shared/http/api-error-message';

@Component({
  selector: 'app-register-step-pay',
  templateUrl: './register-step-pay.component.html',
  styleUrls: ['./register-step-pay.component.scss'],
})
export class RegisterStepPayComponent implements OnInit, OnDestroy {
  @Input() paymentErrors: any = {};
  @Input() preFilledPayData: RegisterFormPay;
  @Input() registerUserData: RegisterForm;
  @Input() referralRef: string | null = null;

  @Output() Submit: EventEmitter<RegisterForm> =
    new EventEmitter<RegisterForm>();

  @ViewChild('buttonComponent') buttonComponent: ButtonComponent;
  @ViewChild('peContainer', { static: false })
  peContainer: ElementRef<HTMLDivElement>;

  registerForm: FormGroup;

  inputTooltipOptions: InputTooltipOptions = {
    text: 'Card Identification Number(CID)',
  };

  currentValidTeamCode: ISubscriptionItem;

  noTeamCodeCharge = '249';

  months: ISelectItem[] = [];

  currentMonth: number;

  years: ISelectItem[] = [];
  currentYear: number;

  products: IPlanProduct[] = [];

  selectedPlan: IPlanProduct;

  componentReady = false;

  private teamCodeSubscribed: Subscription;
  private peTeardown: (() => void) | null = null;
  private confirmSetup:
    | (() => Promise<{ error?: unknown; setupIntent?: { id?: string } }>)
    | null = null;

  paymentElementReady = false;
  peConfirming = false;
  peCompleting = false;
  private setupIntentId: string | null = null;

  private initAccountId: number | null = null;

  constructor(
    private store: Store<PageState>,
    private readonly cdr: ChangeDetectorRef,
    private readonly discountService: DiscountsService,
    private readonly userService: UserService,
    private readonly stripeEmbedded: StripeEmbeddedService,
    private readonly registerStore: Store<IReducers>,
  ) {
    this.store.dispatch(setBannerComponent({ banner: 'Payment' }));
    this.discountService.getPlanProducts()
      .subscribe(data => {
        this.products = data.items;
        this.onPlanChange(6);
        this.componentReady = true;
      });
  }

  get plan_amount() {
    return this.registerForm.get('plan_amount');
  }

  get amount() {
    return this.registerForm.get('amount');
  }

  get discount() {
    return this.registerForm.get('discount');
  }

  getPlanItems(): ISelectItem[] {
    return this.products.map(p => ({
      label: p.name,
      value: p.plan_id,
    }));
  }

  buildInitPayload(): Record<string, any> | null {
    if (!this.registerUserData) {
      return null;
    }
    const raw = this.registerForm.getRawValue();
    let amount = raw.amount;
    if (typeof amount === 'string' && amount.indexOf('$') !== -1) {
      amount = amount.substr(1);
    }
    return {
      ...this.registerUserData,
      ref: this.referralRef || this.registerUserData?.ref || undefined,
      plan_id: raw.plan_id,
      discount: raw.discount || undefined,
      amount: Number(amount) || 0,
      plan_amount: raw.plan_amount,
    };
  }

  submitForm = () => {
    if (!this.registerForm.valid || !this.registerUserData) {
      this.registerForm.markAllAsTouched();
      this.buttonComponent?.release();
      return;
    }
    this.peTeardown?.();
    this.peTeardown = null;
    this.confirmSetup = null;
    this.paymentElementReady = false;
    this.initAccountId = null;
    this.setupIntentId = null;
    this.peConfirming = false;
    this.peCompleting = false;

    const payload = this.buildInitPayload();
    this.userService.registerPaymentElementInit(payload!).subscribe(
      async res => {
        this.buttonComponent?.release();
        const cs = res?.client_secret;
        const pk =
          res?.stripe_publishable_key ||
          (environment as any).stripePublishableKey;
        if (!cs || !pk) {
          this.registerStore.dispatch(
            openSnackbar({
              snackbarData: {
                data: {
                  type: 'error',
                  message:
                    'Could not start payment. Ensure STRIPE_PUBLISHABLE_KEY is set on the server.',
                },
              },
            }),
          );
          return;
        }
        this.initAccountId = res.account_id;
        this.paymentElementReady = true;
        // #peContainer lives under *ngIf="paymentElementReady"; run CD before mount or ViewChild is still null.
        this.cdr.detectChanges();
        setTimeout(() => void this.mountPe(cs, pk), 0);
      },
      err => {
        this.buttonComponent?.release();
        const msg = getApiErrorMessage(err, 'Request failed');
        this.registerStore.dispatch(
          openSnackbar({
            snackbarData: {
              data: { type: 'error', message: msg },
            },
          }),
        );
      },
    );
  };

  private async mountPe(
    clientSecret: string,
    publishableKey: string,
    attempt = 0,
  ) {
    const el = this.peContainer?.nativeElement;
    if (!el) {
      if (attempt < 8) {
        this.cdr.detectChanges();
        setTimeout(
          () => void this.mountPe(clientSecret, publishableKey, attempt + 1),
          0,
        );
        return;
      }
      this.registerStore.dispatch(
        openSnackbar({
          snackbarData: {
            data: {
              type: 'error',
              message:
                'Could not show the payment form. Please refresh the page and try again.',
            },
          },
        }),
      );
      return;
    }
    this.peTeardown?.();
    const mounted = await this.stripeEmbedded.mountPaymentElementForSetupIntent(
      clientSecret,
      el,
      publishableKey,
    );
    if (!mounted) {
      this.registerStore.dispatch(
        openSnackbar({
          snackbarData: {
            data: {
              type: 'error',
              message: 'Could not load Stripe.js (Payment Element).',
            },
          },
        }),
      );
      return;
    }
    this.peTeardown = mounted.destroy;
    this.confirmSetup = mounted.confirm;
  }

  private completeSubscription(setupIntentId: string) {
    if (this.peCompleting || this.initAccountId == null) {
      return;
    }
    this.peCompleting = true;
    const planId = this.registerForm.get('plan_id')?.value;
    const disc = this.registerForm.get('discount')?.value;
    this.userService
      .registerPaymentElementComplete({
        account_id: this.initAccountId,
        setup_intent_id: setupIntentId,
        plan_id: planId,
        discount: disc || undefined,
      })
      .subscribe(
        full => {
          this.peCompleting = false;
          const data = (full as any).data !== undefined ? (full as any).data : full;
          this.registerStore.dispatch(paySuccess({ paymentData: data as any }));
        },
        err => {
          this.peCompleting = false;
          this.registerStore.dispatch(
            openSnackbar({
              snackbarData: {
                data: {
                  type: 'error',
                  message: getApiErrorMessage(err, 'Payment could not be completed'),
                },
              },
            }),
          );
        },
      );
  }

  confirmPeAndSubscribe = async () => {
    // If Stripe already confirmed successfully, do NOT reconfirm (Stripe will often error).
    // Allow retrying only the backend completion call.
    if (this.setupIntentId) {
      this.completeSubscription(this.setupIntentId);
      return;
    }

    if (this.peConfirming || this.peCompleting || !this.confirmSetup || this.initAccountId == null) {
      return;
    }
    this.peConfirming = true;
    const out = await this.confirmSetup();
    this.peConfirming = false;
    if (out.error) {
      this.registerStore.dispatch(
        openSnackbar({
          snackbarData: {
            data: {
              type: 'error',
              message: (out.error as any)?.message || 'Payment failed',
            },
          },
        }),
      );
      return;
    }
    const sid = out.setupIntent?.id;
    if (!sid) {
      this.registerStore.dispatch(
        openSnackbar({
          snackbarData: {
            data: {
              type: 'error',
              message: 'Missing setup intent. Try again.',
            },
          },
        }),
      );
      return;
    }
    this.setupIntentId = sid;
    this.completeSubscription(sid);
  };

  getMonths() {
    return moment.months();
  }

  getYears() {
    const currentYear = moment().year();
    const result = [currentYear];
    for (let i = currentYear; i < currentYear + 16; i++) {
      result.push(i);
    }
    return result;
  }

  ngOnDestroy(): void {
    if (this.teamCodeSubscribed) {
      this.teamCodeSubscribed.unsubscribe();
    }
    this.peTeardown?.();
  }

  getPreFilledData(property) {
    if (!this.preFilledPayData) {
      return null;
    }

    return this.preFilledPayData.hasOwnProperty(property)
      ? this.preFilledPayData[property]
      : null;
  }

  ngOnInit() {
    window.scroll(0, 0);
    const months = this.getMonths();
    for (let i = 0; i < months.length; i++) {
      this.months.push({
        label: months[i],
        value: i + 1,
      });
    }
    this.currentMonth = moment().month() + 1;

    const years = this.getYears();
    for (const item of years) {
      this.years.push({
        label: item.toString(),
        value: item,
      });
    }
    this.currentYear = moment().year();
    this.registerForm = new FormGroup({
      discount: new FormControl(this.getPreFilledData('discount') || ''),
      amount: new FormControl('$' + this.noTeamCodeCharge),
      plan_amount: new FormControl(),
      email: new FormControl(localStorage.getItem('email')),
      plan_id: new FormControl(6, [Validators.required]),
    });
  }

  onPlanChange($event: any) {
    this.selectedPlan = this.products.find((p) => p.plan_id === $event);
  }
}
